import { useEffect, useRef, useState } from "react";

/**
 * Tracks which of several dropdowns (by key) is open, driven by both hover
 * and click/keyboard. Only one dropdown is open at a time.
 *
 * react-bootstrap positions the menu via Popper as position:absolute, so it
 * renders geometrically outside the toggle's own bounding box — moving the
 * mouse from the toggle down into the menu crosses a gap that isn't part of
 * the toggle, which fires mouseleave (and would close the menu) before the
 * cursor ever reaches it. Delaying the actual close in scheduleClose gives
 * that movement time to land on the menu's own onMouseEnter (open again),
 * which cancels the pending close before it fires.
 */
export function useHoverDropdown() {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const open = (key: string) => {
    clearCloseTimer();
    setOpenKey(key);
  };

  const scheduleClose = (key: string) => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpenKey((current) => (current === key ? null : current));
    }, 200);
  };

  useEffect(() => clearCloseTimer, []);

  return { openKey, open, scheduleClose, setOpenKey };
}
